/**
 * "Needs you": what is waiting on this person, across their campaigns (or one campaign). Each row: what, which
 * campaign, by when, one button. The button opens the page with the item (or the kickoff form); an answer from
 * Beatly opens the conversation it belongs to and leaves the list.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, CheckCircle2, FileText, MessageSquareText, Users, Video, Wallet, Radio } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { lykkeApi, type NeedItem, type NeedKind } from './lykkeApi';
import { lykkePath } from './lykkePages';
import { useLykke } from './LykkeContext';

/** The button's word (the row already says what it is). */
const SHORT: Record<NeedKind, string> = { payments: 'Release', drafts: 'Review', creators: 'Review', brief: 'Open', question: 'Read', status: 'Open' };
const ICON: Record<NeedKind, typeof Users> = { brief: FileText, creators: Users, drafts: Video, payments: Wallet, question: MessageSquareText, status: Radio };

// eslint-disable-next-line react-refresh/only-export-components -- hooks used with the list
export function useNeedsYou(enabled: boolean, campaignId?: string | null) {
  return useQuery({
    queryKey: ['lykke', 'needs', campaignId || 'all'],
    queryFn: () => lykkeApi.needsYou(campaignId),
    enabled,
    staleTime: 30_000,
    refetchInterval: 90_000,
    retry: 1,
    select: (d) => d.items,
  });
}

/** What the sidebar badge counts: decisions and answers, not news. */
// eslint-disable-next-line react-refresh/only-export-components -- hooks used with the list
export const actionable = (items: NeedItem[] | undefined) => (items || []).filter((i) => i.kind !== 'status');

// eslint-disable-next-line react-refresh/only-export-components -- hooks used with the list
export function useRunNeed() {
  const navigate = useNavigate();
  const l = useLykke();
  return (item: NeedItem) => {
    if (item.action.href) { window.open(item.action.href, '_blank', 'noopener'); return; }
    if (item.kind === 'question') {
      const id = item.action.params?.requestId;
      if (id) void lykkeApi.seen(id);
      navigate(item.campaignId ? `/lykke/${item.campaignId}` : '/lykke');
      return;
    }
    const to = lykkePath(item.action.page, item.action.params || {}, item.action.focus || {});
    // The page is the point: the conversation dock must not cover its buttons.
    l?.closeDock();
    if (to) navigate(to);
  };
}

export function NeedsYouList({ items, limit, compact, loading, hideCampaign }: { items: NeedItem[]; limit?: number; compact?: boolean; loading?: boolean; /** Inside one campaign: its name on every row says nothing. */ hideCampaign?: boolean }) {
  const run = useRunNeed();
  const shown = typeof limit === 'number' ? items.slice(0, limit) : items;
  if (loading) {
    return <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-[58px] animate-pulse rounded-xl bg-[#f2f4f7]" />)}</div>;
  }
  if (!shown.length) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-[#f9fafb] px-3 py-3 text-[13px] text-[#667085]">
        <CheckCircle2 className="h-4 w-4 text-[#3b5a00]" /> Nothing needs you right now.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-lykke-needs>
      {shown.map((item) => {
        const Icon = ICON[item.kind] || FileText;
        return (
          <li key={item.key} className={cn('flex items-center gap-3 rounded-xl border border-[#eaecf0] bg-white', compact ? 'px-3 py-2' : 'px-3.5 py-2.5')} data-need-kind={item.kind}>
            <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', item.urgent ? 'bg-[#be43ff]/10 text-[#7a1fbf]' : 'bg-[#f2f4f7] text-[#475467]')}><Icon className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-semibold text-[#101828]">{item.title}</div>
              {item.campaignName && !hideCampaign && <div className="truncate text-[12px] text-[#667085]">{item.campaignName}</div>}
              {item.dueLabel && (
                <div className={cn('flex items-center gap-1 text-[12px]', item.urgent ? 'font-semibold text-[#7a1fbf]' : 'text-[#667085]')}>
                  <CalendarClock className="h-3 w-3" />{item.dueLabel}
                </div>
              )}
            </div>
            <button type="button" onClick={() => run(item)} title={item.action.label} aria-label={`${item.action.label}${item.campaignName ? `, ${item.campaignName}` : ''}`}
              className={cn('h-8 shrink-0 rounded-full px-3 text-[12.5px] font-semibold transition-colors', item.kind === 'status' ? 'border border-[#d0d5dd] text-[#344054] hover:bg-[#f9fafb]' : 'bg-[#be43ff] text-white hover:bg-[#a830e0]')}>
              {compact ? (item.kind === 'brief' && item.action.href ? 'Answer' : SHORT[item.kind]) : item.action.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
